import React, { FC, ChangeEvent} from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepButton from "@material-ui/core/StepButton";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import moment, { Moment } from "moment-timezone";
import { CalendarResource } from "../../model/calendarResource";
import { PropertyResource } from "../../model/propertyResource";
import { TourCalendar } from "./tour-calendar";
import { GuestInfo, GuestInfoErrors } from "../../model/guest-info";
import GuestInfoCard from "./guest-info-card";
import { createCalendarResources, minStartHourOfDay, maxEndHourOfDay, NONE_ID } from "../../utils/calendarUtils";
import PropertyService from "../../service/property-service";
import ReviewAppointment from "./review-appointment";
import ConfirmedAppointments from "./confirmed-appointments";
import BookingService, { BookingRequest, AppointmentsResponse } from "../../service/booking-service";
import { FIELD_STEPS, TourStep } from "./tour-step";
import { Grid, Card, CardContent } from "@material-ui/core";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import Loader from "react-loader-spinner";
import d from "debug";
import { SELF_GUIDED_TOUR, CONTACT_ME_TOUR } from "../constants";
import { TOUR, VIRTUAL_TOUR } from "../../service/types";
import FormControl from "@material-ui/core/FormControl";
import Autocomplete, { AutocompleteChangeReason, AutocompleteChangeDetails } from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";

const debug = d("TourBooking");

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      marginLeft: "8px",
      marginRight: "8px",
    },
    stepper: {
      width: "100%",
      paddingTop: "0px",
      paddingBottom: "0px",
      paddingLeft: "0px",
      paddingRight: "80px",
    },
    formControl: {
      width: "100%",
      minWidth: 80
    },
    formInput: {
      width: "100%",
      backgroundColor: "white",
      minWidth: 80
    },
    primaryButton: {
      color: "white",
      borderRadius: "0px",
      boxShadow: "none",
      backgroundColor: "#EF4E22",
      "&:hover": {
        backgroundColor: "#AB3825",
        boxShadow: "none",
      },
    },
    secondaryButton: {
      color: "#CC3600",
      borderRadius: "0px",
      backgroundColor: "white",
      "&:hover": {
        backgroundColor: "rgba(239,78,34, 0.20)",
      },
      marginRight: theme.spacing(1),
    },
    completed: {
      display: "inline-block",
    },
    stepperContent: {
      width: "100%",
      minHeight: "400px",
    },
    screenCenter: {
      position: "fixed",
      top: "50%",
      left: "50%",
      width: "104px",
      height: "104px",
      marginTop: "-52px" /*set to a negative number 1/2 of your height*/,
      marginLeft: "-52px" /*set to a negative number 1/2 of your width*/,
      zIndex: 10000,
    },
  })
);

export interface TourBookingProps {
  refPropertyResource: PropertyResource;
  allPropertyResources: PropertyResource[];
  extraPropertyIds?: string[];
  multiProp: boolean;
  maxCalProps: number;
  radiusMiles: number;
  guestInfoInput: GuestInfo;
  attributionId?: string;
  webClientId?: string;
  onSiteTours: boolean;
  contactMeToSchedule: boolean;
  tourStartDate: Moment;
  advBookHours: number;
  callingMode: string;
  numberOfWeeks: number;
  bookedById: string;
  bookingRefName: string;
  tourAction: string;
  callTrxId: string;
  startDayOfWeek: number;
  numberDisplayDays: number;
  viewDate: Moment;
  calendarResources?: CalendarResource[];
  allCities: PropertyResource[];
  propertySelected: (selectedProperty?: PropertyResource) => void;
  doReset: () => void;
}

const TourBookingField: FC<TourBookingProps> = ({
  refPropertyResource,
  allPropertyResources,
  extraPropertyIds,
  multiProp,
  maxCalProps,
  radiusMiles,
  guestInfoInput,
  attributionId,
  webClientId,
  onSiteTours,
  contactMeToSchedule,
  tourStartDate,
  advBookHours,
  callingMode,
  numberOfWeeks,
  bookedById,
  bookingRefName,
  tourAction,
  callTrxId,
  startDayOfWeek,
  numberDisplayDays,
  viewDate,
  allCities,
  propertySelected,
  doReset
}) => {
    console.log('Calling Mode: ' + callingMode);
  const classes = useStyles();
  const submitRef: React.RefObject<HTMLButtonElement> = React.createRef<HTMLButtonElement>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState(new Set<number>());
  const [tourSteps, setTourSteps] = React.useState<TourStep[]>(FIELD_STEPS);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [propertyResources, setPropertyResources] = React.useState<PropertyResource[]>([]);
  const [allPropResources, setAllPropResources] = React.useState<PropertyResource[]>(allPropertyResources);
  const [calendarResources, setCalendarResources] = React.useState<CalendarResource[]>([]);
  const [selfGuidedPropertyResources, setSelfGuidedPropertyResources] = React.useState<PropertyResource[]>([]);
  const [selfGuidedAppoinments, setSelfGuidedAppoinments] = React.useState<CalendarResource[]>([]);
  const [appoinments, setAppoinments] = React.useState<CalendarResource[]>([]);
  const [appointmentsResponse, setAppointmentsResponse] = React.useState<AppointmentsResponse>();
  const [resourceSelections, setResourceSelections] = React.useState<Map<string, string>>(new Map<string, string>());
  const [startHour] = React.useState<number>(minStartHourOfDay(refPropertyResource, 9));
  const [endHour] = React.useState<number>(maxEndHourOfDay(refPropertyResource, 17));
  const [multiProperties] = React.useState<boolean>(multiProp);
  const [otherPropertyIds] = React.useState<string[] | undefined | null>(extraPropertyIds);
  const [validateGuestInfo, setValidateGuestInfo] = React.useState<number>(1);
  const [guestInfo, setGuestInfo] = React.useState<GuestInfo>(guestInfoInput);
  const [selectedProperty, setSelectedProperty] = React.useState<PropertyResource | null>();
  const [propertyId, setPropertyId] = React.useState<string | undefined>();
  const [referencePropertyResource, setReferencePropertyResource] = React.useState<PropertyResource>();
  const [selfGuidedTourTag, setSelfGuidedTourTag] = React.useState<string | undefined>();
  const [selfGuidedTourName, setSelfGuidedTourName] = React.useState<string | undefined>();
  const [guestInfoErrors, setGuestInfoErrors] = React.useState<GuestInfoErrors>({
    firstNameError: undefined,
    lastNameError: undefined,
    emailError: undefined,
    phoneError: undefined,
    moveInDateError: undefined,
    bedroomsError: undefined,
    tourTypeError: undefined,
  });

  React.useEffect(() => {
    if (guestInfoInput) {
      setGuestInfo(guestInfoInput);
    }
  }, [guestInfoInput]);

  React.useEffect(() => {
    setLoading(true);
    const propertyService = new PropertyService(tourStartDate, numberOfWeeks, advBookHours);
    if (multiProperties) {
      if (otherPropertyIds && otherPropertyIds.length > 0) {
        const newPropertyResources: PropertyResource[] = [];
        newPropertyResources.push({ ...refPropertyResource });
        otherPropertyIds.forEach((id) => {
          const extraPropResource = propertyService.propertyResourceByPropertyId(id, allPropResources);
          if (extraPropResource) {
            newPropertyResources.push({ ...extraPropResource });
          }
        });
        console.log('Mode - ' + callingMode);
        propertyService.populateTimeSlots(newPropertyResources,callingMode).then((populatedPropertyResources) => {
          setPropertyResources(populatedPropertyResources);
        });
      } else {
        propertyService
          .getNearByProperties(callingMode,refPropertyResource.name, radiusMiles, false, true, allPropResources)
          .then((newPropertyResources) => {
            let array: PropertyResource[] = [];
            if (newPropertyResources && newPropertyResources.length > 0) {
              // eliminate properties without available timeslots
              newPropertyResources = newPropertyResources.filter((p) => p.timeSlots && p.timeSlots.length > 0);
              array = newPropertyResources.sort((a, b) => {
                return a.name === refPropertyResource.name ? -1 : 0; // sort ref prop to top
              });
            }
            if (array && array.length > maxCalProps) {
              // truncate size
              array = array.slice(0, maxCalProps);
            }
            setPropertyResources(array);
          });
      }
    } else {
      setPropertyResources([refPropertyResource]);
    }
    setSelfGuidedTourTag('Self Guided');
    setSelfGuidedTourName('Self Guided Tour');
    setLoading(false);
  }, [
    advBookHours,
    numberOfWeeks,
    refPropertyResource,
    refPropertyResource.name,
    tourStartDate,
    maxCalProps,
    multiProperties,
    otherPropertyIds,
    allPropResources,
    radiusMiles,
    guestInfo
  ]);

  React.useEffect(() => {
    const newCalendarResources = createCalendarResources(propertyResources,advBookHours);
    setCalendarResources(newCalendarResources);
    setTourSteps(FIELD_STEPS);
  }, [propertyResources,advBookHours]);

  React.useEffect(() => {
    const isValidForBookingsFetch = !!guestInfo.emailAddress;
    const bookingsStep = stepByIndex(2);
    if (bookingsStep) {
      bookingsStep.error = !isValidForBookingsFetch ? "Guest email must be provided" : undefined;
    }
    const appointmentsStep = stepByIndex(0);
    if (appointmentsStep) {
        appointmentsStep.completed = appoinments.length > 0;
    }
    setTourSteps(tourSteps.map((obj) => ({ ...obj })));
  }, [guestInfo, guestInfoErrors]);

  const validateGuestInfoForm = (): void => {
    setValidateGuestInfo(Math.random() * 1000);
  };

  const anyGuestInfoErrors = (): boolean => {
    return (
      !!guestInfoErrors.firstNameError ||
      !!guestInfoErrors.lastNameError ||
      !!guestInfoErrors.emailError ||
      !!guestInfoErrors.phoneError ||
      //!!guestInfoErrors.moveInDateError ||
      //    !!guestInfoErrors.tourTypeError ||
      !!guestInfoErrors.bedroomsError ||
      !!guestInfoErrors.bathroomsError
    );
  };
  const isCompletedGuestInfo = (): boolean => {
    return (
      !!guestInfo.firstName &&
      !!guestInfo.lastName &&
      !!guestInfo.emailAddress &&
      !!guestInfo.desiredMoveInDate &&
      !!guestInfo.minBedrooms &&
      !!guestInfo.minBathrooms &&
      !!guestInfo.tourType
    );
  };

  const handlePropertySelected = async (selectedProperty?: PropertyResource) => {
    if (selectedProperty) {
      setPropertyId(selectedProperty.propertyId);
      setSelectedProperty(selectedProperty);
      propertySelected(selectedProperty);
    }
    setReferencePropertyResource(selectedProperty);

  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid item xs={12} className={classes.stepperContent}>
            {(
              <TourCalendar
                calendarResources={calendarResources}
                selectedResources={resourceSelections}
                numberOfWeeks={numberOfWeeks}
                startDayOfWeek={startDayOfWeek}
                startHour={startHour}
                endHour={endHour}
                numberDisplayDays={numberDisplayDays}
                viewDate={viewDate}
                resourceSelectionsChange={handleResourceSelectionsChanged}
                allPropertyResources={allPropertyResources}
                propertySelected={handlePropertySelected}
                allCities={allCities}
                refPropertyResource={refPropertyResource}
				        selfGuidedTourTag={selfGuidedTourTag}
				        selfGuidedTourName={selfGuidedTourName}
              />
            )}
          </Grid>
        );
      case 1:
        return (
          <Grid item xs={12} className={classes.stepperContent}>
            <ReviewAppointment
              selectedResources={appoinments}
              guestInfo={guestInfo}
              appointmentsResponse={appointmentsResponse}
            />
          </Grid>
        );
      case 2:
        return (
          <Grid item xs={12} className={classes.stepperContent}>
            <ConfirmedAppointments guestInfo={guestInfo} />
          </Grid>
        );
      default:
        return "Unknown step";
    }
  };

  const totalSteps = () => {
    return tourSteps.length;
  };

  const stepByIndex = (index: number): TourStep | undefined => {
    return tourSteps && index < tourSteps.length ? tourSteps[index] : undefined;
  };

  const skippedSteps = () => {
    return skipped.size;
  };

  const completedSteps = () => {
    return completed.size;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps() - skippedSteps();
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      const newActiveStep = isLastStep() && !allStepsCompleted() ? tourSteps.findIndex((step, i) => !completed.has(i)) : activeStep + 1;
      handleSetStep(newActiveStep);
    } else {
      const newActiveStep = isLastStep() && !allStepsCompleted() ? tourSteps.findIndex((step, i) => !completed.has(i)) : activeStep + 1;
      handleSetStep(newActiveStep);
    }
    
  };

  const handleBack = () => {
    const newActiveStep = Math.max(0, activeStep - 1);
    handleSetStep(newActiveStep);
  };

  const handleSetStep = (step: number) => {
    //if (activeStep === 1 && step !== 1) {
    //  validateGuestInfoForm();
    //}
    setActiveStep(step);
  };

  const handleStep = (step: number) => () => {
    handleSetStep(step);
  };

  const handleSelfGuidedPropertiesChange = (selectedProperties?: PropertyResource[]) => {
    setSelfGuidedPropertyResources(selectedProperties);
    const appoinments: CalendarResource[] = selectedProperties.map((item, index) => {
      return {
        id: item.propertyId,
        propertyId: item.propertyId,
        name: item.name,
        startTime: moment(),
        endTime: moment().add(1, "years"),
      };
    });
    setSelfGuidedAppoinments(appoinments);
    const appointmentsStep = stepByIndex(0);
    if (appointmentsStep) {
      appointmentsStep.completed = appoinments.length > 0;
    }
  };

  const handleResourceSelectionsChanged = (selectionMap: Map<string, string>, selectedResources: CalendarResource[]) => {
    setResourceSelections(selectionMap);
    const filteredMap = new Map<string, string>();
    selectionMap.forEach((value, key) => {
      if (value !== NONE_ID) {
        filteredMap.set(key, value);
      }
    });
    setAppoinments(selectedResources);
    const appointmentsStep = stepByIndex(0);
    if (appointmentsStep) {
      appointmentsStep.completed = filteredMap.size > 0;
    }
  };

  const handleBookAppointments = () => {
    setLoading(true);
    const bookingService = new BookingService();
    const bookingRequest: BookingRequest = {
      firstName: guestInfo.firstName,
      lastName: guestInfo.lastName,
      email: guestInfo.emailAddress,
      phone: guestInfo.mobilePhone,
      minBedrooms: guestInfo.minBedrooms,
      minBathrooms: guestInfo.minBathrooms,
      //desiredMoveInDate: moment(guestInfo.desiredMoveInDate).format("YYYY-MM-DD"),
      desiredMoveInDate: '2025-10-05',
      allowSMSConfirmation: guestInfo.smsOptIn ? "Y" : "N",
      attributionId: guestInfo.attributionId,
      tourNotes: guestInfo.tourNotes,
      webClientId,
      propertyList: [],
      bookedById,
      bookingRefName,
      tourAction,
      callTrxId,
      callingMode
    };
      var newTime;
      appoinments.forEach((a) => { 
        if (moment.tz.guess() === 'America/New_York'){
          newTime = moment(a.startTime).add(3,'hours').toDate();
        } else if (moment.tz.guess() === 'America/Chicago'){
          newTime = moment(a.startTime).add(2,'hours').toDate();
        } else if (moment.tz.guess() === 'America/Denver'){
          newTime = moment(a.startTime).add(1,'hours').toDate();
        } else {
          newTime = moment(a.startTime).toDate();
        } 
        bookingRequest.propertyList.push({
          tourType: appoinments[0].tourType,
          propertyName: a.name,
          tourTime: newTime,
        });
      });
    bookingService
      .bookAppointments(bookingRequest)
      .then((response) => {
        setAppointmentsResponse(undefined);
        setLoading(false);
        if (response.status === 200 && completed.size !== totalSteps() - skippedSteps()) {
          setAppointmentsResponse(response);
          const bookingStep = stepByIndex(2);
          if (bookingStep) {
            bookingStep.completed = true;
          }
        }
        handleRefresh();
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const resetCalendar = () => {
    setAppoinments([]);
    setResourceSelections(new Map<string, string>());
    setAllPropResources([...allPropResources]); // setting all resources will cause re-initialzation
  };

  /**
   * When Guest Tour Type changes to/from in person apartement tour, only show properties with the "allowGuidedTour" flag.
   */
  const handleGuestInfoChanged = (newGuestInfo: GuestInfo, infoErrors: GuestInfoErrors) => {
    setGuestInfoErrors(infoErrors);
    setGuestInfo(newGuestInfo);
  };

  const handleRefresh = () => {
    resetCalendar();
    setTourSteps(
      tourSteps.map((step, index) => {
        const clone = { ...step };
        clone.completed = index === 0 ? step.completed : false;
        clone.error = undefined;
        return clone;
      })
    );
    setCompleted(new Set<number>());
    setSkipped(new Set<number>());
    setAppoinments([]);
    setSelfGuidedAppoinments([]);
    setSelfGuidedPropertyResources([]);
    setResourceSelections(new Map<string, string>());
    doReset();
  };

  return (
    <>
      <Card className={classes.root}>
        <CardContent component="div">
          <Grid container item direction="row" justify="space-around" alignItems="stretch" spacing={1} xs={12}>
            <Grid item xs={12}>
              <Stepper alternativeLabel className={classes.stepper} nonLinear activeStep={activeStep} style={{ width: "100%" }}>
                {tourSteps.map((step, index) => {
                  const stepProps: { completed?: boolean } = {};
                  const buttonProps: { optional?: React.ReactNode } = {};
                  if (step.optional || step.error) {
                    buttonProps.optional = (
                      <>
                        {!step.error && (
                          <Typography variant="body2" noWrap={true}>
                            Optional
                          </Typography>
                        )}
                        {step.error && (
                          <Typography variant="body2" color="error">
                            {step.error}
                          </Typography>
                        )}
                      </>
                    );
                  }
                  return (
                    <Step key={step.label} {...stepProps}>
                      <StepButton
                        style={{ fontFamily: "Mallory", fontSize: "16px", fontWeight: 600 }}
                        onClick={handleStep(index)}
                        completed={step.completed}
                        {...buttonProps}
                      >
                        {step.label}
                      </StepButton>
                    </Step>
                  );
                })}
              </Stepper>
            </Grid>
            <Grid item xs={12}>
              {getStepContent(activeStep)}
              <div>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.secondaryButton}
                >
                  Back
                </Button>
                <Button className={classes.primaryButton} size="large" variant="contained" color="primary" onClick={handleNext}>
                  Next
                </Button>
                {activeStep === 0 && (
                  <Button
                    style={{ marginLeft: "16px", borderRadius: "0px" }}
                    size="large"
                    variant="outlined"
                    color="primary"
                    onClick={handleRefresh}
                  >
                    Refresh Time Slots
                  </Button>
                )}
                {activeStep === 1 && (
                  <Button
                    className={classes.primaryButton}
                    style={{ marginLeft: "16px" }}
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={handleBookAppointments}
                    disabled={
                      loading ||
                      (appoinments.length === 0)
                    }
                  >
                    {"Confirm Appointments"}
                  </Button>
                )}
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <div className={classes.screenCenter} style={loading ? { visibility: "visible" } : { visibility: "hidden" }}>
        <Loader
          visible={loading}
          type="BallTriangle"
          color="#EF4E22"
          height={100}
          width={100}
          timeout={5000} //5 secs
        />
      </div>
    </>
  );
};

export default TourBookingField;